/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

class DcHeaderLevelTwo extends HTMLElement {
  connectedCallback() {
    if (this._dcInitialized) {
      return;
    }

    this._dcInitialized = true;
    this.searchPanel = this.querySelector('[data-dc-search-panel]');
    this.searchTrigger = this.querySelector('[data-dc-search-trigger]');
    this.mobileSearchTrigger = this.querySelector('[data-action="toggle-search"]');
    this.searchClose = this.querySelector('[data-dc-search-close]');
    this.searchForm = this.searchPanel?.querySelector('.search-bar');
    this.searchInput = this.searchPanel?.querySelector('[name="q"]');
    this.searchDiscovery = this.searchPanel?.querySelector('.search-bar__menu-wrapper');
    this.searchResults = this.searchPanel?.querySelector('.search-bar__results');
    this.cartContainer = this.querySelector('.header__action-item--cart');
    this.cartToggle = this.cartContainer?.querySelector('[data-action="toggle-mini-cart"]');
    this.miniCart = this.cartContainer?.querySelector('.mini-cart');
    this.account = this.querySelector('[data-dc-account]');
    this.headerSection = this.closest('.shopify-section');

    this._onLocalClick = this._onLocalClick.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
    this._onSearchInput = this._onSearchInput.bind(this);
    this._onAccountOpen = this._onAccountOpen.bind(this);
    this._onCartRerendered = this._onCartRerendered.bind(this);

    this.addEventListener('click', this._onLocalClick, true);
    document.addEventListener('click', this._onDocumentClick, true);
    document.addEventListener('keydown', this._onDocumentKeydown);
    this.searchInput?.addEventListener('input', this._onSearchInput);
    this.account?.querySelector('shopify-account')?.addEventListener('open', this._onAccountOpen);
    this.cartContainer?.addEventListener('cart:rerendered', this._onCartRerendered);
    document.addEventListener('shopify:section:load', this._onCartRerendered);

    if (this.searchPanel) {
      this.searchPanel.inert = true;
    }

    if (this.miniCart) {
      this._cartObserver = new MutationObserver(() => this._syncCartState());
      this._cartObserver.observe(this.miniCart, { attributes: true, attributeFilter: ['aria-hidden'] });
    }

    this._normalizeCartContent();
    this._syncCartState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onLocalClick, true);
    document.removeEventListener('click', this._onDocumentClick, true);
    document.removeEventListener('keydown', this._onDocumentKeydown);
    this.searchInput?.removeEventListener('input', this._onSearchInput);
    this.account?.querySelector('shopify-account')?.removeEventListener('open', this._onAccountOpen);
    this.cartContainer?.removeEventListener('cart:rerendered', this._onCartRerendered);
    document.removeEventListener('shopify:section:load', this._onCartRerendered);
    this._cartObserver?.disconnect();
    window.clearTimeout(this._searchFocusTimer);
    window.clearTimeout(this._cartFocusTimer);
    this._dcInitialized = false;
  }

  _isDesktop() {
    return window.matchMedia('(min-width: 1000px)').matches;
  }

  _isSearchOpen() {
    return Boolean(
      this.searchPanel &&
        (this.searchPanel.getAttribute('aria-hidden') === 'false' ||
          this.searchPanel.classList.contains('is-visible') ||
          this.searchPanel.classList.contains('is-fixed') ||
          this.searchForm?.classList.contains('is-expanded'))
    );
  }

  _openSearch() {
    if (!this.searchPanel || !this.searchTrigger || !this._isDesktop()) {
      return;
    }

    if (this.cartToggle?.getAttribute('aria-expanded') === 'true') {
      this.cartToggle.click();
    }

    this.searchPanel.inert = false;
    this.searchPanel.setAttribute('aria-hidden', 'false');
    this.searchPanel.classList.add('is-visible');
    this.searchTrigger.setAttribute('aria-expanded', 'true');
    this.searchForm?.classList.add('is-expanded');
    this._syncSearchState();
    this.searchInput?.focus({ preventScroll: true });
    window.requestAnimationFrame(() => this.searchInput?.focus({ preventScroll: true }));
    window.clearTimeout(this._searchFocusTimer);
    this._searchFocusTimer = window.setTimeout(() => this.searchInput?.focus({ preventScroll: true }), 50);
  }

  _closeSearch({ restoreFocus = false } = {}) {
    if (!this.searchPanel) {
      return;
    }

    this.searchPanel.setAttribute('aria-hidden', 'true');
    this.searchPanel.classList.remove('is-visible', 'is-fixed');
    this.searchPanel.inert = true;
    this.searchPanel.removeAttribute('data-has-query');
    this.searchTrigger?.setAttribute('aria-expanded', 'false');
    this.searchForm?.classList.remove('is-expanded', 'is-loading');
    this.searchInput?.classList.remove('is-filled');
    if (this.searchClose) {
      this.searchClose.style.width = '';
    }
    this.searchDiscovery?.setAttribute('aria-hidden', 'true');
    this.searchResults?.setAttribute('aria-hidden', 'true');
    window.clearTimeout(this._searchFocusTimer);

    if (this.cartToggle?.getAttribute('aria-expanded') !== 'true') {
      document.documentElement.classList.remove('is-locked');
    }

    if (restoreFocus) {
      const focusTarget = this._isDesktop() ? this.searchTrigger : this.mobileSearchTrigger;
      focusTarget?.focus();
    }
  }

  _syncSearchState() {
    const hasQuery = Boolean(this.searchInput?.value.trim());
    this.searchPanel?.toggleAttribute('data-has-query', hasQuery);

    if (!hasQuery) {
      this.searchDiscovery?.setAttribute('aria-hidden', 'false');
      this.searchResults?.setAttribute('aria-hidden', 'true');
    } else {
      this.searchDiscovery?.setAttribute('aria-hidden', 'true');
      this.searchResults?.setAttribute('aria-hidden', 'false');
    }
  }

  _onSearchInput() {
    this._syncSearchState();
  }

  _onLocalClick(event) {
    const searchTrigger = event.target.closest('[data-dc-search-trigger]');

    if (searchTrigger) {
      event.preventDefault();
      event.stopPropagation();
      if (this._isSearchOpen()) {
        this._closeSearch({ restoreFocus: true });
      } else {
        this._openSearch();
      }
      return;
    }


    if (event.target.closest('[data-dc-cart-backdrop]')) {
      event.preventDefault();
      this.cartToggle?.click();
      return;
    }

    if (event.target.closest('.header__cart-toggle')) {
      this._closeSearch();
      return;
    }

    if (event.target.closest('[data-dc-account]')) {
      this._closeSearch();
      if (this.cartToggle?.getAttribute('aria-expanded') === 'true') {
        this.cartToggle.click();
      }
      return;
    }

    if (event.target.closest('[data-action="toggle-search"]') && !this._isDesktop()) {
      window.requestAnimationFrame(() => {
        const isVisible = this.searchPanel?.classList.contains('is-visible');
        this.searchPanel?.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        if (this.searchPanel) {
          this.searchPanel.inert = !isVisible;
        }
      });
    }
  }

  _onDocumentClick(event) {
    const searchClose = event.target.closest('[data-dc-search-close]');

    if (searchClose && this.contains(searchClose) && this._isDesktop()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this._closeSearch({ restoreFocus: true });
      return;
    }

    if (event.target.closest('[data-dc-account]')) {
      this._closeSearch();
      if (this.cartToggle?.getAttribute('aria-expanded') === 'true') {
        this.cartToggle.click();
      }
      return;
    }

    if (event.target.closest('.header__cart-toggle')) {
      this._closeSearch();
    }

    if (!this._isSearchOpen()) {
      return;
    }

    if (this.searchPanel?.contains(event.target) || this.searchTrigger?.contains(event.target)) {
      return;
    }

    this._closeSearch();
  }

  _onDocumentKeydown(event) {
    if (event.key === 'Escape' && this._isSearchOpen() && this._isDesktop()) {
      event.preventDefault();
      this._closeSearch({ restoreFocus: true });
    }
  }

  _onAccountOpen() {
    this._closeSearch();
    if (this.cartToggle?.getAttribute('aria-expanded') === 'true') {
      this.cartToggle.click();
    }
  }

  _onCartRerendered() {
    window.requestAnimationFrame(() => this._normalizeCartContent());
  }

  _syncCartState() {
    this._normalizeCartContent();
    const isOpen = this.miniCart?.getAttribute('aria-hidden') === 'false';
    this.headerSection?.classList.toggle('is-dc-cart-open', isOpen);

    if (isOpen) {
      this._closeSearch();
      this._cartWasOpen = true;
      window.clearTimeout(this._cartFocusTimer);
      this._cartFocusTimer = window.setTimeout(() => {
        this.miniCart?.querySelector('.dc-mini-cart__close')?.focus({ preventScroll: true });
      }, 50);
      return;
    }

    if (this._cartWasOpen) {
      window.clearTimeout(this._cartFocusTimer);
      if (window.Accessibility?.removeTrapFocus) {
        window.Accessibility.removeTrapFocus(this.miniCart, 'mini-cart');
      }
      this.cartToggle?.focus();
      this._cartWasOpen = false;
    }
  }

  _normalizeCartContent() {
    if (!this.miniCart) {
      return;
    }

    const source = this.miniCart.dataset;
    const setText = (selector, dataKey) => {
      const element = this.miniCart.querySelector(selector);
      const value = source[dataKey];
      if (element && value) {
        element.textContent = value;
      }
    };

    setText('[data-dc-cart-heading]', 'dcCartHeading');
    setText('[data-dc-cart-empty-text]', 'dcCartEmptyText');
    setText('[data-dc-cart-empty-cta]', 'dcCartEmptyCtaText');
    setText('[data-dc-cart-checkout]', 'dcCartCheckoutText');
    setText('[data-dc-cart-subtotal]', 'dcCartSubtotalText');
    setText('[data-dc-cart-page-link]', 'dcCartPageLinkText');

    const badgeCount = Number.parseInt(this.querySelector('.header__cart-count')?.textContent, 10);
    if (Number.isFinite(badgeCount)) {
      this.miniCart.dataset.itemCount = String(badgeCount);
    }

    const emptyCta = this.miniCart.querySelector('[data-dc-cart-empty-cta]');
    const emptyCtaLink = source.dcCartEmptyCtaLink;
    if (emptyCta && emptyCtaLink) {
      emptyCta.setAttribute('href', emptyCtaLink);
    } else if (emptyCta && !emptyCtaLink) {
      emptyCta.remove();
    }

    const cartPageLink = this.miniCart.querySelector('[data-dc-cart-page-link]');
    if (cartPageLink && source.dcShowCartPageLink !== 'true') {
      cartPageLink.remove();
    }
  }
}

if (!customElements.get('dc-header-level-two')) {
  customElements.define('dc-header-level-two', DcHeaderLevelTwo);
}

class DcMegaMenuController {
  constructor() {
    this.navs = new Map();
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
    this._onSectionLoad = this._onSectionLoad.bind(this);
    this._onSectionUnload = this._onSectionUnload.bind(this);

    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('keydown', this._onDocumentKeydown);
    document.addEventListener('shopify:section:load', this._onSectionLoad);
    document.addEventListener('shopify:section:unload', this._onSectionUnload);
    this.connect(document);
  }

  connect(root) {
    const navs = root.matches?.('.dc-header__nav')
      ? [root]
      : Array.from(root.querySelectorAll?.('.dc-header__nav') || []);

    navs.forEach((nav) => {
      if (this.navs.has(nav)) return;

      const rawHeaderSettings = nav.closest('[data-section-type="header"]')?.dataset.sectionSettings;
      let openTrigger = 'hover';

      try {
        openTrigger = JSON.parse(rawHeaderSettings || '{}').desktopOpenTrigger || openTrigger;
      } catch {
        openTrigger = 'hover';
      }

      const panelsById = new Map();
      const duplicatePanelIds = new Set();

      nav.querySelectorAll('[data-dc-mega-menu]').forEach((panel) => {
        if (!panel.id) return;
        if (panelsById.has(panel.id)) duplicatePanelIds.add(panel.id);
        panelsById.set(panel.id, panel);
      });

      duplicatePanelIds.forEach((id) => panelsById.delete(id));

      const claimedPanels = new Set();
      const pairs = [];

      nav.querySelectorAll('[data-dc-level-3-trigger][aria-controls]').forEach((trigger) => {
        const panel = panelsById.get(trigger.getAttribute('aria-controls'));
        const item = trigger.closest('.dc-header__nav-item');
        const triggerBlockId = trigger.dataset.dcMegaMenuBlockId;
        const panelBlockId = panel?.dataset.dcMegaMenuBlockId;

        if (
          !item ||
          !panel ||
          claimedPanels.has(panel) ||
          !item.contains(panel) ||
          !triggerBlockId ||
          triggerBlockId !== panelBlockId
        ) {
          return;
        }

        claimedPanels.add(panel);
        pairs.push({
          item,
          trigger,
          panel,
          pointerInside: false,
          focusInside: false
        });
      });

      const state = {
        nav,
        openTrigger,
        pairs,
        pairsByItem: new Map(pairs.map((pair) => [pair.item, pair])),
        activePair: null
      };

      state.onMouseOver = (event) => this._onMouseOver(event, state);
      state.onMouseOut = (event) => this._onMouseOut(event, state);
      state.onFocusIn = (event) => this._onFocusIn(event, state);
      state.onFocusOut = (event) => this._onFocusOut(event, state);

      nav.addEventListener('mouseover', state.onMouseOver, true);
      nav.addEventListener('mouseout', state.onMouseOut, true);
      nav.addEventListener('focusin', state.onFocusIn, true);
      nav.addEventListener('focusout', state.onFocusOut, true);
      this.navs.set(nav, state);
    });
  }

  _pairFromTarget(state, target) {
    if (!(target instanceof Element)) return null;
    const item = target.closest('.dc-header__nav-item');
    return state.pairsByItem.get(item) || null;
  }

  _onMouseOver(event, state) {
    if (state.openTrigger !== 'hover' || !window.matchMedia('(hover: hover)').matches) return;

    const pair = this._pairFromTarget(state, event.target);
    if (!pair) return;

    event.stopImmediatePropagation();
    if (pair.item.contains(event.relatedTarget)) return;

    pair.pointerInside = true;
    this._openPair(state, pair);
  }

  _onMouseOut(event, state) {
    if (state.openTrigger !== 'hover' || !window.matchMedia('(hover: hover)').matches) return;

    const pair = this._pairFromTarget(state, event.target);
    if (!pair) return;

    event.stopImmediatePropagation();
    if (pair.item.contains(event.relatedTarget)) return;

    pair.pointerInside = false;
    if (!pair.focusInside) this._closePair(state, pair);
  }

  _onFocusIn(event, state) {
    const pair = this._pairFromTarget(state, event.target);
    if (!pair) return;

    event.stopImmediatePropagation();
    pair.focusInside = true;
    this._openPair(state, pair);
  }

  _onFocusOut(event, state) {
    const pair = this._pairFromTarget(state, event.target);
    if (!pair) return;

    event.stopImmediatePropagation();
    if (pair.item.contains(event.relatedTarget)) return;

    pair.focusInside = false;
    if (!pair.pointerInside) this._closePair(state, pair);
  }

  _setPairState(pair, isOpen) {
    pair.item.classList.toggle('is-dropdown-open', isOpen);
    pair.trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    pair.panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    const menu = pair.item.closest('[data-type="menu"]');
    if (!menu) return;

    if (isOpen) {
      menu.classList.add('nav-dropdown--glued');
    } else if (!menu.querySelector('.is-dropdown-open')) {
      menu.classList.remove('nav-dropdown--glued');
    }
  }

  _openPair(activeState, activePair) {
    this.navs.forEach((state) => {
      state.pairs.forEach((pair) => {
        if (pair === activePair) return;
        pair.pointerInside = false;
        pair.focusInside = false;
        this._setPairState(pair, false);
      });

      if (state !== activeState) state.activePair = null;
    });

    this._setPairState(activePair, true);
    activeState.activePair = activePair;
  }

  _closePair(state, pair) {
    this._setPairState(pair, false);
    if (state.activePair === pair) state.activePair = null;
  }

  _closeAll({ restoreFocus = false } = {}) {
    let triggerToRestore = null;

    if (restoreFocus) {
      this.navs.forEach((state) => {
        if (!triggerToRestore && state.activePair) triggerToRestore = state.activePair.trigger;
      });
      triggerToRestore?.focus({ preventScroll: true });
    }

    this.navs.forEach((state) => {
      state.pairs.forEach((pair) => {
        pair.pointerInside = false;
        pair.focusInside = false;
        this._setPairState(pair, false);
      });
      state.activePair = null;
    });
  }

  _onDocumentClick(event) {
    const isInsideNavigation = Array.from(this.navs.keys()).some((nav) => nav.contains(event.target));
    if (!isInsideNavigation) this._closeAll();
  }

  _onDocumentKeydown(event) {
    if (event.key !== 'Escape') return;

    const hasOpenMegaMenu = Array.from(this.navs.values()).some((state) => state.activePair);
    if (!hasOpenMegaMenu) return;

    event.preventDefault();
    this._closeAll({ restoreFocus: true });
  }

  _onSectionLoad(event) {
    this.connect(event.target);
  }

  _onSectionUnload(event) {
    const navs = event.target.matches?.('.dc-header__nav')
      ? [event.target]
      : Array.from(event.target.querySelectorAll?.('.dc-header__nav') || []);

    navs.forEach((nav) => {
      const state = this.navs.get(nav);
      if (!state) return;

      nav.removeEventListener('mouseover', state.onMouseOver, true);
      nav.removeEventListener('mouseout', state.onMouseOut, true);
      nav.removeEventListener('focusin', state.onFocusIn, true);
      nav.removeEventListener('focusout', state.onFocusOut, true);
      this.navs.delete(nav);
    });
  }
}

function initializeDcMegaMenuController() {
  if (!window.dcMegaMenuController) {
    window.dcMegaMenuController = new DcMegaMenuController();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeDcMegaMenuController, { once: true });
} else {
  initializeDcMegaMenuController();
}

class DcRevealSystem {
  constructor() {
    this.selector = '.dc-reveal';
    this.observer = new IntersectionObserver(this._onIntersect.bind(this), {
      root: null,
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.12
    });

    this._onSectionLoad = this._onSectionLoad.bind(this);
    document.addEventListener('shopify:section:load', this._onSectionLoad);
    this.observe(document);
  }

  observe(root) {
    const elements = root.matches?.(this.selector)
      ? [root]
      : Array.from(root.querySelectorAll?.(this.selector) || []);

    if (elements.length === 0) {
      return;
    }

    document.documentElement.classList.add('dc-reveal-ready');

    elements.forEach((element) => {
      if (!element.classList.contains('is-revealed')) {
        this.observer.observe(element);
      }
    });
  }

  _onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return;
      }

      entry.target.classList.add('is-revealed');
      this.observer.unobserve(entry.target);
    });
  }

  _onSectionLoad(event) {
    this.observe(event.target);
  }
}

function initializeDcRevealSystem() {
  const rootStyles = window.getComputedStyle(document.documentElement);
  const isEnabled = Number.parseInt(rootStyles.getPropertyValue('--dc-reveal-enabled'), 10) === 1;

  if (!isEnabled || !('IntersectionObserver' in window) || window.dcRevealSystem) {
    return;
  }

  window.dcRevealSystem = new DcRevealSystem();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeDcRevealSystem, { once: true });
} else {
  initializeDcRevealSystem();
}

class DcHeroMedia extends HTMLElement {
  connectedCallback() {
    this.videos = Array.from(this.querySelectorAll('[data-dc-hero-video]'));
    this.desktopMediaQuery = window.matchMedia('(min-width: 700px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplay = this.dataset.autoplay === 'true';
    this.controls = this.dataset.controls === 'true';
    this._onMediaChange = this._update.bind(this);

    this.desktopMediaQuery.addEventListener('change', this._onMediaChange);
    this.reducedMotionQuery.addEventListener('change', this._onMediaChange);
    this._update();
  }

  disconnectedCallback() {
    this.desktopMediaQuery?.removeEventListener('change', this._onMediaChange);
    this.reducedMotionQuery?.removeEventListener('change', this._onMediaChange);
    this.videos?.forEach((video) => video.pause());
  }

  _activateSources(video) {
    if (video.dataset.sourcesReady === 'true') return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.dataset.sourcesReady = 'true';
  }

  _update() {
    const isDesktop = this.desktopMediaQuery.matches;
    const viewport = isDesktop ? 'desktop' : 'mobile';
    const activeVideo = this.videos.find((video) => video.dataset.viewport === viewport)
      || (!isDesktop ? this.videos.find((video) => video.dataset.viewport === 'desktop') : null);

    this.videos.forEach((video) => {
      const isActive = video === activeVideo;
      video.hidden = !isActive;
      if (!isActive) video.pause();
    });

    if (!activeVideo) return;

    const poster = isDesktop ? this.dataset.desktopPoster : this.dataset.mobilePoster;
    if (poster) activeVideo.poster = poster;

    const reducedMotion = this.reducedMotionQuery.matches;
    const shouldLoad = this.controls || (this.autoplay && !reducedMotion);
    if (!shouldLoad) {
      activeVideo.pause();
      activeVideo.preload = 'none';
      return;
    }

    this._activateSources(activeVideo);
    activeVideo.preload = this.autoplay && !reducedMotion ? 'auto' : 'metadata';

    if (activeVideo.readyState === 0) activeVideo.load();

    if (this.autoplay && !reducedMotion) {
      activeVideo.muted = true;
      const playPromise = activeVideo.play();
      if (playPromise) playPromise.catch(() => {});
    } else {
      activeVideo.pause();
    }
  }
}

if (!customElements.get('dc-hero-media')) {
  customElements.define('dc-hero-media', DcHeroMedia);
}
